import {
  useBottomNavigator,
  useIsProcessingQuickConnect,
} from "utils/hooks/misc.tsx";
import { sharedLib } from "@shared/services/shared.service.ts";
import {
  selectClientToExchangeMapping,
  selectConsolidatedPortfolioData,
  selectPortfolioDataLoadingStates,
  selectPortfolioPrecision,
} from "@shared/slices/portfolio.slice.ts";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { isContractsSymbol } from "@shared/utils/misc.ts";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { PageTabsContext } from "common/Tabs/PageTabs.context";
import {
  ConsolidatedCoinData,
  GetPortfolioDataResponse,
  PortfolioTab,
  QueryStatus,
} from "@shared/types";
import { AnalyticsContext } from "analytics/AnalyticsProvider.tsx";
import { usePortfolioPieChart } from "@shared/features/portfolio/hooks";
import { DrawerContext } from "common/Drawers/Drawer.context";
import { skipToken } from "@reduxjs/toolkit/query";
import { PrecisionConfig } from "types";
import { PortfolioContext } from "features/portfolio/Portfolio.context.ts";

interface usePortfolioDataProps {
  dataSelector?: (state: any) => ConsolidatedCoinData[];
  loadingSelector?: (state: any) => { loading: boolean; loaded: boolean };
  precisionSelector?: (state: any) => PrecisionConfig | undefined;
  getPortfolioDataQuery?: QueryStatus<GetPortfolioDataResponse>;
  isPositions?: boolean;
}

const usePortfolioData = ({
  dataSelector: propsDataSelector,
  loadingSelector: propsLoadingSelector,
  precisionSelector: propsPrecisionSelector,
  getPortfolioDataQuery: propsGetPortfolioDataQuery,
  isPositions,
}: usePortfolioDataProps) => {
  const { analytics } = useContext(AnalyticsContext);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { setTabCount } = useContext(PageTabsContext) || {};
  const { isDrawer } = useContext(DrawerContext) || {};
  const { setTabToNumItems } = useContext(PortfolioContext) || {};
  const isProcessingQuickConnect = useIsProcessingQuickConnect();

  const getPortfolioDataQuery =
    sharedLib.sharedApi.portfolio.useGetPortfolioDataQuery(
      !!propsGetPortfolioDataQuery || isProcessingQuickConnect
        ? skipToken
        : undefined
    );

  const { isLoading: getPortfolioDataLoading, refetch } =
    propsGetPortfolioDataQuery ?? getPortfolioDataQuery;

  const dataSelector = propsDataSelector ?? selectConsolidatedPortfolioData;
  const loadingSelector =
    propsLoadingSelector ?? selectPortfolioDataLoadingStates;
  const precisionSelector = propsPrecisionSelector ?? selectPortfolioPrecision;
  const data = useSelector(dataSelector);
  const clientToExchangeMapping = useSelector(selectClientToExchangeMapping);
  const { loading, loaded } = useSelector(loadingSelector);
  const { isNewLook } = useTheme();
  // The new look counts only what has loaded (no "(0)" while loading).
  const countable = loaded || !isNewLook;

  const { total, margin, pieChartData, positionsPieChartData } =
    usePortfolioPieChart({
      dataSelector,
      nameHeader: "symbol",
    });

  const numOfPositions = useMemo(
    () => data.filter((coin) => isContractsSymbol(coin.symbol)).length,
    [data]
  );
  const numOfCoins = data.length - numOfPositions;

  useEffect(() => {
    setTabToNumItems?.((prev) => ({
      ...prev,
      [PortfolioTab.ASSETS]: countable ? numOfCoins : undefined,
      [PortfolioTab.POSITIONS]: countable ? numOfPositions : undefined,
    }));
  }, [numOfCoins, numOfPositions, countable]);

  useEffect(() => {
    analytics?.portfolioCoinsView({});
  }, [analytics]);

  useEffect(() => {
    const count = isPositions ? numOfPositions : numOfCoins;
    setTabCount?.(
      isPositions ? PortfolioTab.POSITIONS : PortfolioTab.ASSETS,
      countable ? count : undefined
    );
  }, [isPositions, data, countable]);

  const onRefresh = useCallback(() => {
    try {
      refetch();
    } catch {}
  }, []);

  return {
    isBottomNavigator,
    getPortfolioDataLoading,
    onRefresh,
    numOfCoins,
    numOfPositions,
    dataSelector,
    pieChartData,
    positionsPieChartData,
    total,
    margin,
    isDrawer,
    precisionSelector,
    clientToExchangeMapping,
    loading,
    loaded,
  };
};

export default usePortfolioData;
